// src/support/tileDisplayLayoutUtils.ts

import { ensureNumber } from './utils';
import { TileDisplayContext } from './tileDisplayContextUtils';

export type MarginPresetKey =
  | 'dashboardBaseMargins'
  | 'dashboardLegendMargins'
  | 'leaderBaseMargins'
  | 'leaderLegendMargins';

export type ResolvedTileDisplayContext = 'dashboard' | 'leader';

export type MarginSettings = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type MarginCapableSettings = {
  show_legend?: boolean;

  baseMargins?: Partial<MarginSettings>;
  legendMargins?: Partial<MarginSettings>;

  dashboardBaseMargins?: Partial<MarginSettings>;
  dashboardLegendMargins?: Partial<MarginSettings>;
  leaderBaseMargins?: Partial<MarginSettings>;
  leaderLegendMargins?: Partial<MarginSettings>;

  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
};

export function normalizeMarginSettings(value: any): MarginSettings | null {
  if (!value || typeof value !== 'object') return null;

  return {
    top: ensureNumber(value.top, 24),
    right: ensureNumber(value.right, 16),
    bottom: ensureNumber(value.bottom, 48),
    left: ensureNumber(value.left, 16),
  };
}

export function resolveDisplayContext(
  displayContext: TileDisplayContext,
  previewDisplayContext?: 'dashboard' | 'leader'
): ResolvedTileDisplayContext {
  if (displayContext === 'leader') return 'leader';
  if (displayContext === 'dashboard') return 'dashboard';
  return previewDisplayContext === 'leader' ? 'leader' : 'dashboard';
}

export function getMarginPresetKey(
  displayContext: TileDisplayContext,
  showLegend: boolean,
  previewDisplayContext?: 'dashboard' | 'leader'
): MarginPresetKey {
  const context = resolveDisplayContext(displayContext, previewDisplayContext);

  if (context === 'leader') {
    return showLegend ? 'leaderLegendMargins' : 'leaderBaseMargins';
  }

  return showLegend ? 'dashboardLegendMargins' : 'dashboardBaseMargins';
}

export function getStoredMarginsForContext(
  settings: MarginCapableSettings,
  displayContext: TileDisplayContext,
  opts?: {
    previewDisplayContext?: 'dashboard' | 'leader';
    showLegend?: boolean;
  }
): MarginSettings {
  const showLegend = opts?.showLegend ?? !!settings.show_legend;
  const resolvedContext = resolveDisplayContext(displayContext, opts?.previewDisplayContext);

  if (resolvedContext === 'leader') {
    if (showLegend) {
      return (
        normalizeMarginSettings(settings.leaderLegendMargins) ??
        normalizeMarginSettings(settings.legendMargins) ??
        normalizeMarginSettings(settings.leaderBaseMargins) ??
        normalizeMarginSettings(settings.baseMargins) ?? {
          top: ensureNumber(settings.marginTop, 24),
          right: ensureNumber(settings.marginRight, 16),
          bottom: ensureNumber(settings.marginBottom, 48),
          left: ensureNumber(settings.marginLeft, 16),
        }
      );
    }

    return (
      normalizeMarginSettings(settings.leaderBaseMargins) ??
      normalizeMarginSettings(settings.baseMargins) ??
      normalizeMarginSettings(settings.leaderLegendMargins) ??
      normalizeMarginSettings(settings.legendMargins) ?? {
        top: ensureNumber(settings.marginTop, 24),
        right: ensureNumber(settings.marginRight, 16),
        bottom: ensureNumber(settings.marginBottom, 48),
        left: ensureNumber(settings.marginLeft, 16),
      }
    );
  }

  if (showLegend) {
    return (
      normalizeMarginSettings(settings.dashboardLegendMargins) ??
      normalizeMarginSettings(settings.legendMargins) ??
      normalizeMarginSettings(settings.dashboardBaseMargins) ??
      normalizeMarginSettings(settings.baseMargins) ?? {
        top: ensureNumber(settings.marginTop, 24),
        right: ensureNumber(settings.marginRight, 16),
        bottom: ensureNumber(settings.marginBottom, 48),
        left: ensureNumber(settings.marginLeft, 16),
      }
    );
  }

  return (
    normalizeMarginSettings(settings.dashboardBaseMargins) ??
    normalizeMarginSettings(settings.baseMargins) ??
    normalizeMarginSettings(settings.dashboardLegendMargins) ??
    normalizeMarginSettings(settings.legendMargins) ?? {
      top: ensureNumber(settings.marginTop, 24),
      right: ensureNumber(settings.marginRight, 16),
      bottom: ensureNumber(settings.marginBottom, 48),
      left: ensureNumber(settings.marginLeft, 16),
    }
  );
}